
import { useGlobalDiscoveryFacets } from "@entities/global-discovery/hooks/useGlobalDiscoveryFacets";
import { useGetGlobalDiscoveryStore } from "@entities/global-discovery/store/useGetGlobalDiscoveryStoreV2";
import { ConfigGlobalTypeSingle } from "@shared/lib/types";
import { SelectButton } from "@shared/ui/Select";
import { useEffect, useMemo, useState } from "react";
import styles from "./MultiListFilter.module.scss";

interface MultiListFilterProps {
  filter: ConfigGlobalTypeSingle;
  showTitle?: boolean;
  selectedFilters?: any;
  setSelectedFilter?: (updateFn: (value: any) => any) => void;
  platformId?: string;
  options?: { label: string; value: string }[];
  loading?: boolean;
}

export function MultiListFilter({
  filter,
  showTitle = true,
  selectedFilters: selectedFiltersProp,
  setSelectedFilter: setSelectedFilterProp,
  platformId: platformIdProp,
  options: optionsProp,
  loading: loadingProp,
}: MultiListFilterProps) {
  const globalStore = useGetGlobalDiscoveryStore();
  const selectedFilters = selectedFiltersProp ?? globalStore.selectedFilters;
  const setSelectedFilter = setSelectedFilterProp ?? globalStore.setSelectedFilter;
  const platformId = platformIdProp ?? globalStore.platformId;

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch facet options from backend
  const { facet, isFetching: isFacetsFetching } = useGlobalDiscoveryFacets({
    facetName: filter.facetName,
    body: {},
    query: debouncedQuery,
    work_platform_id: platformId,
    enabled: !optionsProp && !!platformId,
  });

  const isFetching = optionsProp ? !!loadingProp : isFacetsFetching;

  // Convert facet data to Select options, including selected values not in facets
  const options = useMemo(() => {
    if (optionsProp) {
      if (searchQuery) {
        return optionsProp.filter((opt) =>
          opt.label.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }
      return optionsProp;
    }

    const facetOptions = (facet || []).map((item) => ({
      label: item.name,
      value: item.value,
    }));

    const facetValues = new Set(facetOptions.map((opt) => opt.value));
    const selectedValues = Array.isArray(selectedFilters[filter.facetName])
      ? (selectedFilters[filter.facetName] as string[])
      : [];
    const filterTags = selectedFilters.filterTags || {};
    const labelsFromFilterTags = Array.isArray(filterTags[filter.facetName])
      ? (filterTags[filter.facetName] as string[])
      : [];

    // Add selected values that are not in facet results
    const missingOptions: Array<{ label: string; value: string }> = [];
    selectedValues.forEach((value, index) => {
      if (!facetValues.has(value)) {
        const label = labelsFromFilterTags[index] || value;
        missingOptions.push({ label, value });
      }
    });

    return [...facetOptions, ...missingOptions];
  }, [facet, selectedFilters, filter.facetName, optionsProp, searchQuery]);

  // Get current selected values (SelectButton uses simple array of values)
  const currentValue = useMemo(() => {
    const values = Array.isArray(selectedFilters[filter.facetName])
      ? (selectedFilters[filter.facetName] as string[])
      : [];
    return values.map((value) => String(value));
  }, [selectedFilters, filter.facetName]);

  const handleChange = (values: string[] | undefined, options: any) => {
    // SelectButton onChange receives array of values directly (not labelInValue format)

    if (!values || values.length === 0) {
      setSelectedFilter((prevFilters) => {
        return {
          ...prevFilters,
          [filter.facetName]: [],
          filterTags: {
            ...(prevFilters.filterTags || {}),
            [filter.facetName]: [],
          },
        };
      });
      return;
    }

    // Handle array format (multi-select always returns array)
    const selectedValues = values;
    const selectedOptions = Array.isArray(options) ? options : [];
    const labels = selectedOptions.map(
      (item) => item?.label || item?.value || ""
    );

    setSelectedFilter((prevFilters) => {
      return {
        ...prevFilters,
        [filter.facetName]: selectedValues,
        filterTags: {
          ...(prevFilters.filterTags || {}),
          [filter.facetName]: labels,
        },
      };
    });
  };

  const handleSelect = () => {
    // Clear search query immediately when an option is selected
    setSearchQuery("");
  };

  const handleSearch = (value: string) => {
    setSearchQuery(value);
  };

  const handleDropdownVisibleChange = (open: boolean) => {
    // Clear search query when dropdown closes
    if (!open) {
      setSearchQuery("");
    }
  };

  return (
    <div className={styles.multiListFilter}>
      {showTitle && <label className={styles.label}>{filter.title}</label>}
      <SelectButton
        prefixIcon={!showTitle ? filter.title + ": " : undefined}
        size="middle"
        className={styles.select}
        mode="multiple"
        placeholder={filter.placeHolder || "Select options"}
        options={options}
        value={currentValue}
        onChange={handleChange}
        onSelect={handleSelect}
        onSearchChange={handleSearch}
        onDropdownVisibleChange={handleDropdownVisibleChange}
        searchValue={searchQuery || undefined}
        searchable
        loading={isFetching}
        withFooterActions
        allowClear={false}
      />
    </div>
  );
}
